import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import TrustPillars from '../components/TrustPillars';
import EmailCaptureForm from '../components/EmailCaptureForm';
import PulseTeaser from '../components/PulseTeaser';
import TeamGrid from '../components/TeamGrid';
import HowItWorks from '../components/HowItWorks';
import Arrow from '../components/Arrow';
import { SplitText, CountUp, ShinyText, Magnet, SpotlightCard } from '../reactbits';
import { useLanguage } from '../context/LanguageContext';
import { fetchLatestPost } from '../api';

const COMPARISON = {
  old: [
    'Commission-driven distribution - the seller earns from what you buy',
    'Jargon-heavy reports that bury the why',
    'One-size-fits-all model portfolios',
    'Finfluencer noise and hot tips',
  ],
  napho: [
    'Fiduciary advice - SEBI RIA route, answerable only to you',
    'Every recommendation explained in plain language',
    'Guidance shaped to your goals, horizon and comfort with risk',
    'One calm, daily Market Pulse - signal over noise',
  ],
};

export default function Home() {
  const { t, lang } = useLanguage();
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    let active = true;
    fetchLatestPost(lang)
      .then((r) => active && setLatest(r.post))
      .catch(() => active && setLatest(null));
    return () => { active = false; };
  }, [lang]);

  const marqueeItems = [
    t('trust.1.title'),
    t('trust.2.title'),
    t('trust.3.title'),
    t('pulse.eyebrow'),
    t('hero.pill'),
  ];

  const aWords = t('hero.title.a').trim().split(/\s+/).length;

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="hero">
        <div className="orb orb--1" aria-hidden />
        <div className="orb orb--2" aria-hidden />

        <div className="container hero__grid">
          <div>
            <span className="hero__pill">{t('hero.pill')}</span>
            <h1 className="hero__title">
              <SplitText text={t('hero.title.a')} delay={0.05} />
              <em className="hero__title-em">
                <ShinyText text={t('hero.title.em')} speed={4} />
              </em>{' '}
              <SplitText text={t('hero.title.b')} startDelay={aWords * 0.05 + 0.15} delay={0.05} />
            </h1>
            <p className="hero__lead">{t('hero.lead')}</p>
            <div className="hero__actions">
              <div style={{ minWidth: 'min(400px, 100%)' }}>
                <EmailCaptureForm source="hero" />
              </div>
            </div>
            <p className="hero__trustline">{t('hero.trustline')}</p>

            <div className="hero__stats">
              <div className="hero__stat">
                <b>SEBI RIA</b>
                <span>Fiduciary route</span>
              </div>
              <div className="hero__stat">
                <b><CountUp to={7} duration={1.6} /> languages</b>
                <span>Your language first</span>
              </div>
              <div className="hero__stat">
                <b>Daily</b>
                <span>Market Pulse brief</span>
              </div>
            </div>
          </div>

          <div>
            <PulseTeaser post={latest} />
          </div>
        </div>
      </section>

      {/* ── Scrolling trust marquee ──────────────────────────── */}
      <div className="marquee" aria-hidden>
        <div className="marquee__track">
          {Array(6).fill(marqueeItems).flat().map((item, i) => (
            <span className="marquee__item" key={i}>
              {item} <span className="marquee__sep">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── Trust pillars ────────────────────────────────────── */}
      <Section alt eyebrow={t('trust.eyebrow')} title={t('trust.title')}>
        <TrustPillars />
      </Section>

      {/* ── How it works ─────────────────────────────────────── */}
      <Section eyebrow={t('how.eyebrow')} title={t('how.title')} lead={t('how.lead')}>
        <HowItWorks />
      </Section>

      {/* ── The difference (dezerv-style VS comparison) ──────── */}
      <Section eyebrow={t('why.eyebrow')} title={t('why.title')} lead={t('problem.body')}>
        <div className="vs">
          <SpotlightCard className="vs__col vs__col--old">
            <h3 className="vs__head">The old way</h3>
            <ul>
              {COMPARISON.old.map((item) => (
                <li key={item}><span className="vs__mark vs__mark--x">✕</span>{item}</li>
              ))}
            </ul>
          </SpotlightCard>

          <div className="vs__badge" aria-hidden>vs</div>

          <SpotlightCard className="vs__col vs__col--new">
            <h3 className="vs__head">The Napho way</h3>
            <ul>
              {COMPARISON.napho.map((item) => (
                <li key={item}><span className="vs__mark vs__mark--ok">✓</span>{item}</li>
              ))}
            </ul>
          </SpotlightCard>
        </div>
      </Section>

      {/* ── Vision pull-quote ────────────────────────────────── */}
      <Section alt>
        <blockquote className="quote-band">
          <p>“{t('vision.body')}”</p>
          <footer>- {t('vision.title')}, Napho</footer>
        </blockquote>
      </Section>

      {/* ── Daily brief / newsletter ─────────────────────────── */}
      <Section>
        <div className="callout callout--accent">
          <div className="grid grid-2" style={{ alignItems: 'center', position: 'relative' }}>
            <div>
              <h2 style={{ marginBottom: '0.6rem', fontSize: 'clamp(1.5rem, 3vw, 2.1rem)' }}>
                <span className="callout__dot" aria-hidden />{t('news.title')}
              </h2>
              <p style={{ marginBottom: '1.5rem' }}>{t('news.lead')}</p>
              <Link to="/market-pulse" className="btn btn--ghost btn--sm">
                {t('cta.viewAll')} <Arrow className="btn__arrow" />
              </Link>
            </div>
            <div>
              <EmailCaptureForm mode="newsletter" source="home-newsletter" />
            </div>
          </div>
        </div>
      </Section>

      {/* ── Ask Napho CTA ────────────────────────────────────── */}
      <Section alt eyebrow={t('ask.eyebrow')} title={t('ask.title')} lead={t('ask.lead')}>
        <Magnet padding={120} magnetStrength={4}>
          <Link to="/ask" className="btn btn--primary">
            {t('nav.ask')} <Arrow className="btn__arrow" />
          </Link>
        </Magnet>
      </Section>

      {/* ── Team ─────────────────────────────────────────────── */}
      <Section eyebrow={t('team.eyebrow')} title={t('team.title')}>
        <TeamGrid />
      </Section>
    </>
  );
}
